// Cameras and microphones for the studio: constraints tuned for recording,
// device lists, Bluetooth detection, and a built-in test signal so a room
// can be exercised (and verified) without real devices.

export interface DeviceChoice {
  micId?: string;
  camId?: string;
  /** On headphones there's no echo to cancel, so leave the voice untouched. */
  headphones: boolean;
  video: boolean;
}

export function constraintsFor(c: DeviceChoice): MediaStreamConstraints {
  const process = !c.headphones;
  return {
    audio: {
      ...(c.micId ? { deviceId: { exact: c.micId } } : {}),
      echoCancellation: process,
      noiseSuppression: process,
      autoGainControl: process,
      channelCount: 1,
      sampleRate: 48_000,
    },
    video: c.video
      ? {
          ...(c.camId ? { deviceId: { exact: c.camId } } : {}),
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        }
      : false,
  };
}

export interface Devices {
  mics: MediaDeviceInfo[];
  cams: MediaDeviceInfo[];
}

/** Labels are only filled in after permission has been granted. */
export async function listDevices(md: MediaDevices = navigator.mediaDevices): Promise<Devices> {
  const all = await md.enumerateDevices();
  return { mics: all.filter((d) => d.kind === 'audioinput'), cams: all.filter((d) => d.kind === 'videoinput') };
}

const BLUETOOTH = /bluetooth|airpods|hands-?free|headset|buds|bose|jabra|beats|\bbt\b/i;

/**
 * Bluetooth mics fall back to narrowband "headset" audio (8–16 kHz) while
 * the mic is open, which ruins a recording. Spot them by name or rate.
 */
export function bluetoothRisk(label: string, sampleRate?: number): boolean {
  return BLUETOOTH.test(label) || (sampleRate !== undefined && sampleRate > 0 && sampleRate <= 16_000);
}

/**
 * iPhone and iPad (iPadOS reports itself as a Mac with touch). Safari there
 * stops recording when the tab goes to the background.
 */
export function isIOS(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1);
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((t) => t.stop());
}

/** How far ahead test clicks are put on the audio clock (survives timer throttling). */
export const CLICK_LOOKAHEAD_MS = 1500;

export interface TestSignal {
  stream: MediaStream;
  stop(): void;
}

export const SYNTH_RATE = 48_000;
export const TICK_MS = 20;
/**
 * Audio goes to the track in 10 ms frames: Chrome's audio pipeline works in
 * 10 ms buffers and drops most of the audio in frames of other sizes
 * (measured on prod: ~70% lost with 20 ms-ish frames, none with 10 ms).
 */
export const FRAME_MS = 10;
const FRAME = (SYNTH_RATE * FRAME_MS) / 1000;

/** Calls `fn` every `ms`, from a worker when possible: hidden tabs throttle page timers, not worker messages. */
export function workerTicker(ms: number, fn: () => void): () => void {
  try {
    const url = URL.createObjectURL(new Blob([`setInterval(() => postMessage(0), ${ms});`], { type: 'text/javascript' }));
    const worker = new Worker(url);
    worker.onmessage = fn;
    return () => {
      worker.terminate();
      URL.revokeObjectURL(url);
    };
  } catch {
    const t = setInterval(fn, ms);
    return () => clearInterval(t);
  }
}

/**
 * The test tone and clicks, sample by sample, from `fromMs` for `count`
 * samples: a quiet tone, plus a 10 ms 1 kHz click starting on every
 * server-clock second.
 */
export function synthSamples(fromMs: number, count: number, hz: number, phase: number): { data: Float32Array<ArrayBuffer>; phase: number } {
  const data = new Float32Array(count);
  const step = (2 * Math.PI * hz) / SYNTH_RATE;
  for (let i = 0; i < count; i++) {
    const t = fromMs + (i * 1000) / SYNTH_RATE;
    const intoSecond = ((t % 1000) + 1000) % 1000;
    data[i] = 0.05 * Math.sin(phase) + (intoSecond < 10 ? 0.6 * Math.sin(2 * Math.PI * t) : 0);
    phase += step;
  }
  return { data, phase: phase % (2 * Math.PI) };
}

type GeneratorCtor = new (init: { kind: 'audio' }) => MediaStreamTrack & { writable: WritableStream<AudioData> };

/**
 * The test audio without Web Audio: synthesized in step with the server
 * clock and fed to a track generator. Web Audio won't start until someone
 * clicks the page (autoplay rules), which automated checks can't do.
 */
export function synthAudioTrack(opts: {
  hz: number;
  serverNow: () => number;
  Generator: GeneratorCtor;
  AudioDataImpl?: typeof AudioData;
  ticker?: typeof workerTicker;
}): { track: MediaStreamTrack; stop: () => void } {
  const gen = new opts.Generator({ kind: 'audio' });
  const writer = gen.writable.getWriter();
  let last = opts.serverNow();
  let phase = 0;
  let timestampUs = 0;
  const tick = () => {
    const now = opts.serverNow();
    // At most a second at a time; a longer stall restarts from now.
    if (now - last > 1000) last = now - 1000;
    while (now - last >= FRAME_MS) {
      const out = synthSamples(last, FRAME, opts.hz, phase);
      phase = out.phase;
      void writer
        .write(new (opts.AudioDataImpl ?? AudioData)({ format: 'f32-planar', sampleRate: SYNTH_RATE, numberOfFrames: FRAME, numberOfChannels: 1, timestamp: timestampUs, data: out.data }))
        .catch(() => undefined);
      timestampUs += FRAME_MS * 1000;
      last += FRAME_MS;
    }
  };
  const stopTicks = (opts.ticker ?? workerTicker)(TICK_MS, tick);
  return {
    track: gen,
    stop() {
      stopTicks();
      void writer.close().catch(() => undefined);
    },
  };
}

/**
 * A synthetic camera + mic: a quiet tone at a per-participant pitch, a short
 * 1 kHz click on every server-clock second (so tracks can be checked for
 * alignment), and a canvas showing the name and server clock.
 */
export function testSignal(opts: {
  name: string;
  hz: number;
  serverNow: () => number;
  doc?: Document;
  AudioContextImpl?: typeof AudioContext;
  Generator?: GeneratorCtor | null;
  ticker?: typeof workerTicker;
}): TestSignal {
  const Ctx = opts.AudioContextImpl ?? AudioContext;
  const ctx = new Ctx();
  const Generator =
    opts.Generator === undefined ? ((globalThis as { MediaStreamTrackGenerator?: GeneratorCtor }).MediaStreamTrackGenerator ?? null) : opts.Generator;
  // Without a click on the page Web Audio stays silent; synthesize instead.
  const synth = ctx.state === 'suspended' && Generator ? synthAudioTrack({ hz: opts.hz, serverNow: opts.serverNow, Generator, ticker: opts.ticker }) : null;
  const out = ctx.createMediaStreamDestination();
  const tone = ctx.createOscillator();
  const toneGain = ctx.createGain();
  tone.frequency.value = opts.hz;
  toneGain.gain.value = 0.05;
  tone.connect(toneGain).connect(out);
  tone.start();

  // Clicks are scheduled on the audio clock, ahead of time: timers jitter
  // and background tabs throttle them to once a second.
  let scheduledUpTo = Math.floor(opts.serverNow() / 1000);
  const scheduleClicks = () => {
    const now = opts.serverNow();
    const horizon = Math.floor((now + CLICK_LOOKAHEAD_MS) / 1000);
    for (let s = scheduledUpTo + 1; s <= horizon; s++) {
      const at = ctx.currentTime + (s * 1000 - now) / 1000;
      if (at < ctx.currentTime) continue;
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.frequency.value = 1000;
      env.gain.setValueAtTime(0, 0);
      env.gain.setValueAtTime(0.6, at);
      env.gain.setValueAtTime(0, at + 0.01);
      osc.connect(env).connect(out);
      osc.start(at);
      osc.stop(at + 0.02);
    }
    scheduledUpTo = Math.max(scheduledUpTo, horizon);
  };
  scheduleClicks();
  const clickTimer = setInterval(scheduleClicks, 250);

  const doc = opts.doc ?? document;
  const canvas = doc.createElement('canvas');
  canvas.width = 640;
  canvas.height = 360;
  const g = canvas.getContext('2d');
  const hue = Math.round(opts.hz) % 360;
  const draw = () => {
    if (!g) return;
    g.fillStyle = `hsl(${hue} 45% 25%)`;
    g.fillRect(0, 0, 640, 360);
    g.fillStyle = '#fff';
    g.font = '28px system-ui, sans-serif';
    g.fillText(opts.name, 32, 60);
    g.font = '48px ui-monospace, monospace';
    g.fillText(new Date(opts.serverNow()).toISOString().slice(11, 23), 32, 200);
  };
  draw();
  // Worker-driven, so the picture keeps moving in a background tab.
  const stopDrawing = (opts.ticker ?? workerTicker)(33, draw);
  const video = (canvas as HTMLCanvasElement & { captureStream(fps?: number): MediaStream }).captureStream(30);

  const audio = synth ? [synth.track] : out.stream.getAudioTracks();
  const stream = new MediaStream([...audio, ...video.getVideoTracks()]);
  return {
    stream,
    stop() {
      clearInterval(clickTimer);
      stopDrawing();
      synth?.stop();
      stopStream(stream);
      tone.stop();
      void ctx.close();
    },
  };
}

/** A distinct, pleasant test pitch per participant (220–440 Hz). */
export function pitchFor(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return 220 + (h % 221);
}
